import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { Button, Card, Chip, Dialog, FormField, Input, Select, toast } from '@/ui';
import {
  BROADCAST_AUDIENCES,
  BROADCAST_BODY_MAX,
  BROADCAST_TITLE_MAX,
  fetchBroadcasts,
  sendBroadcast,
  type BroadcastAudience,
  type BroadcastDelivery,
  type BroadcastResult,
} from './api';
import { adminKeys } from './query-keys';
import { invalidateAfterBroadcast } from './invalidation';
import { AUDIENCE_LABEL_KEYS } from './labels';
import { adminErrorMessage } from './error-message';
import { formatDateTime } from './format';
import { ListStatus, LoadMore } from './ListStatus';
import { Textarea } from './Textarea';
import { flattenPages, usePagedQuery } from './use-paged-query';
import styles from './Admin.module.css';

/**
 * DUYURU BESTECISI + GECMISI.
 *
 * ============================ NEREDE DURUYOR ===============================
 * Menude ayri bir "duyurular" ogesi YOK; bu bolum metrikler sayfasinin ALTINDA
 * duruyor (`/yonetim/metrikler#duyuru`). Sebep sirf yer degil: duyuru
 * gondermeden once bakilacak sey tam olarak o sayfadaki sayilar — kac musteri,
 * kac buro, ne kadar hareket var.
 *
 * ============================== GERI ALINAMAZ ==============================
 * Gonderilen bildirim HEDEF KITLENIN TAMAMINA gider ve geri cagrilamaz. Bu
 * yuzden:
 *   1. Metin, telefonda gorunecegi bicimde ONIZLENIR.
 *   2. Gonderim bir ONAY DIYALOGUNUN arkasindadir ve diyalog hedef kitleyi
 *      ADIYLA soyler.
 *   3. Basliktaki/govdedeki sinirlar (100 / 500) sunucununkiyle AYNI ve
 *      sayaclar yazarken gorunur — sunucudan 400 almak ogrenmenin en pahali
 *      yolu.
 *
 * ====================== "successCount" BIR TESLIMAT SAYISI DEGIL ===========
 * TOPIC kipinde sunucu FCM'e tek bir konu mesaji yolluyor; donen
 * `successCount: 1` "FCM konuyu KABUL ETTI" demektir — "1 cihaza ulasti"
 * DEGIL. Kac cihazin aldigi bu kipte OLCULEMEZ. Sonuc paneli bu yuzden topic
 * kipinde sayi degil, konu adlarini ve bir aciklama gosterir; token kipinde
 * (tek kullaniciya gonderim) gercek cihaz sayisi anlamlidir ve gosterilir.
 */

const isAudience = (value: string): value is BroadcastAudience =>
  (BROADCAST_AUDIENCES as readonly string[]).includes(value);

export function BroadcastComposer() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sectionId = useId();

  const [audience, setAudience] = useState<BroadcastAudience>('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState<BroadcastResult | null>(null);

  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  const titleOver = trimmedTitle.length > BROADCAST_TITLE_MAX;
  const bodyOver = trimmedBody.length > BROADCAST_BODY_MAX;
  const canSend =
    trimmedTitle.length > 0 && trimmedBody.length > 0 && !titleOver && !bodyOver;

  const send = useMutation({
    mutationFn: sendBroadcast,
    onSuccess: async (result) => {
      setLastResult(result);
      setTitle('');
      setBody('');
      toast({ message: t('admin.broadcast.sent'), tone: 'success' });
      await invalidateAfterBroadcast(queryClient);
    },
    onError: (error: unknown) => {
      toast({ message: adminErrorMessage(error, t), tone: 'danger' });
    },
  });

  const history = usePagedQuery(adminKeys.broadcasts(), (pageParam) => fetchBroadcasts(pageParam));
  const rows = flattenPages(history.data);

  return (
    <section className={styles.page} aria-labelledby={`${sectionId}-heading`}>
      <Card padding="md">
        <h2 id={`${sectionId}-heading`} className={styles.sectionTitle}>
          {t('admin.broadcast.title')}
        </h2>
        <p className={styles.warningNotice}>{t('admin.broadcast.warning')}</p>

        <div className={styles.composer}>
          <div className={styles.composerGrid}>
            <FormField label={t('admin.broadcast.audienceLabel')}>
              <Select
                value={audience}
                options={BROADCAST_AUDIENCES.map((value) => ({
                  value,
                  // Sabit HARITA anahtar tutar; ceviri BURADA, render sirasinda.
                  label: t(AUDIENCE_LABEL_KEYS[value] ?? value),
                }))}
                onChange={(event) => {
                  const value = event.target.value;
                  if (isAudience(value)) setAudience(value);
                }}
              />
            </FormField>

            <FormField
              label={t('admin.broadcast.titleLabel')}
              {...(titleOver ? { error: t('admin.errors.titleTooLong') } : {})}
            >
              <Input
                value={title}
                maxLength={BROADCAST_TITLE_MAX + 20}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
              />
            </FormField>
          </div>
          <span className={[styles.counter, titleOver ? styles.counterOver : ''].join(' ')}>
            {trimmedTitle.length} / {BROADCAST_TITLE_MAX}
          </span>

          <FormField
            label={t('admin.broadcast.bodyLabel')}
            {...(bodyOver ? { error: t('admin.errors.bodyTooLong') } : {})}
          >
            <Textarea
              value={body}
              maxLength={BROADCAST_BODY_MAX + 50}
              onChange={(event) => {
                setBody(event.target.value);
              }}
            />
          </FormField>
          <span className={[styles.counter, bodyOver ? styles.counterOver : ''].join(' ')}>
            {trimmedBody.length} / {BROADCAST_BODY_MAX}
          </span>

          {/* ONIZLEME — gonderilecek sey bu. Kitle de burada tekrar gorunur. */}
          <div>
            <span className={styles.detailLabel}>{t('admin.broadcast.previewLabel')}</span>
            <div className={styles.preview}>
              <Chip tone="info" size="sm">
                {t(AUDIENCE_LABEL_KEYS[audience] ?? audience)}
              </Chip>
              <span className={styles.previewTitle}>
                {trimmedTitle === '' ? t('admin.broadcast.previewEmptyTitle') : trimmedTitle}
              </span>
              <span className={styles.previewBody}>
                {trimmedBody === '' ? t('admin.broadcast.previewEmptyBody') : trimmedBody}
              </span>
            </div>
          </div>

          <div className={styles.actionRow}>
            <Button
              iconLeft={<Megaphone size={16} aria-hidden="true" />}
              disabled={!canSend || send.isPending}
              onClick={() => {
                setConfirmOpen(true);
              }}
            >
              {t('admin.broadcast.sendAction')}
            </Button>
          </div>
        </div>
      </Card>

      {lastResult === null ? null : (
        <Card padding="md">
          <h3 className={styles.sectionTitle}>{t('admin.broadcast.resultTitle')}</h3>
          <DeliverySummary delivery={lastResult.delivery} />
        </Card>
      )}

      <Card padding="md">
        <h3 className={styles.sectionTitle}>{t('admin.broadcast.historyTitle')}</h3>

        <ListStatus
          isPending={history.isPending}
          isError={history.isError}
          error={history.error}
          isEmpty={history.isSuccess && rows.length === 0}
          emptyTitle={t('admin.broadcast.historyEmpty')}
          onRetry={() => {
            void history.refetch();
          }}
        />

        {rows.length > 0 ? (
          <>
            <div className={styles.rowList}>
              {rows.map((row) => (
                <div key={row.id} className={styles.auditRow}>
                  <div className={styles.badges}>
                    <span className={styles.auditAction}>{row.title}</span>
                    <Chip tone="neutral" size="sm">
                      {t(AUDIENCE_LABEL_KEYS[row.audience] ?? row.audience)}
                    </Chip>
                  </div>
                  <span className={styles.previewBody}>{row.body}</span>
                  <span className={styles.muted}>
                    {row.actorFullName} · {formatDateTime(row.createdAt)}
                  </span>
                  <DeliverySummary delivery={row.delivery} />
                </div>
              ))}
            </div>
            <LoadMore
              hasNextPage={history.hasNextPage}
              isFetchingNextPage={history.isFetchingNextPage}
              onLoadMore={() => {
                void history.fetchNextPage();
              }}
            />
          </>
        ) : null}
      </Card>

      <Dialog
        open={confirmOpen}
        tone="danger"
        title={t('admin.broadcast.confirmTitle')}
        description={t('admin.broadcast.confirmDescription', {
          audience: t(AUDIENCE_LABEL_KEYS[audience] ?? audience),
          title: trimmedTitle,
        })}
        confirmLabel={t('admin.broadcast.sendAction')}
        cancelLabel={t('common.cancel')}
        busy={send.isPending}
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          setConfirmOpen(false);
          send.mutate({
            audience,
            title: trimmedTitle,
            body: trimmedBody,
            // Hedefli gonderim bu ekrandan yapilmiyor: kitleye gonderim ile tek
            // kisiye gonderim ayni formda olsaydi yanlis olani secmek kolaydi.
            targetUserId: null,
          });
        }}
      />
    </section>
  );
}

/**
 * TESLIMAT OZETI. Topic kipinde SAYI GOSTERILMEZ — bkz. dosya basi.
 */
function DeliverySummary({ delivery }: { delivery: BroadcastDelivery }) {
  const { t } = useTranslation();

  if (delivery.mode === 'topic') {
    return (
      <span className={styles.muted}>
        {t('admin.broadcast.topicDelivery', {
          topics: delivery.topics.join(', ') || '—',
        })}
      </span>
    );
  }

  return (
    <span className={styles.muted}>
      {t('admin.broadcast.tokenDelivery', {
        tokens: delivery.tokenCount ?? 0,
        success: delivery.successCount,
        failure: delivery.failureCount,
      })}
    </span>
  );
}
